import { createContext, useEffect, useState } from 'react'
import { GlobalStyles } from 'tss-react'
import { createTheme, StyledEngineProvider, ThemeProvider, useColorScheme } from '@mui/material/styles'
import { ThemeModes } from '../../enums'

declare module '@mui/material/Checkbox' {
  export interface CheckboxPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }
}
declare module '@mui/material/Fab' {
  export interface FabPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }
}
declare module '@mui/material/Button' {
  export interface ButtonPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }

  export interface ButtonPropsVariantOverrides {
    assetPrimary: true
    assetSecondary: true
  }
}

declare module '@mui/material/IconButton' {
  export interface IconButtonPropsColorOverrides {
    primaryText: true
    secondaryText: true
    greyScale: true
    primaryWhite: true
  }
}

declare interface VesselColorToken {
  main: string
  light?: string
  dark?: string
  text?: string
  contrastText?: string
  background?: string
  hover?: string
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    main: string
    dark: string
  }

  export interface PaletteOptions {
    primaryText?: VesselColorToken
    primaryWhite?: VesselColorToken
    secondaryText?: VesselColorToken
    complementaryBlue?: VesselColorToken
    paperOnPaper?: VesselColorToken
    activeGreen?: VesselColorToken
    alternateRowGrey?: VesselColorToken
    calmRed?: VesselColorToken
    mainWarning?: VesselColorToken
    errorText?: VesselColorToken
    textLink?: VesselColorToken
    primarySelected?: VesselColorToken
    alternateRowsEven?: VesselColorToken
    alternateRowsOdd?: VesselColorToken
    darkIcon?: VesselColorToken
    greyScale?: VesselColorToken
    greyPrimary?: VesselColorToken
    alertbg?: VesselColorToken
    activeButtonBackground?: VesselColorToken
    defaultText?: VesselColorToken
    inverseText?: VesselColorToken
    card?: {
      background?: VesselColorToken
      border?: VesselColorToken
      text?: VesselColorToken
    }
    shadow?: VesselColorToken
    icon?: VesselColorToken
    container?: VesselColorToken
    dropdownMenuGroup?: VesselColorToken
    white?: string
    black?: string
  }

  export interface Palette {
    primaryText: VesselColorToken
    primaryWhite: VesselColorToken
    secondaryText: VesselColorToken
    complementaryBlue: VesselColorToken
    paperOnPaper: VesselColorToken
    activeGreen: VesselColorToken
    alternateRowGrey: VesselColorToken
    calmRed: VesselColorToken
    mainWarning: VesselColorToken
    errorText: VesselColorToken
    textLink: VesselColorToken
    primarySelected: VesselColorToken
    alternateRowsEven: VesselColorToken
    alternateRowsOdd: VesselColorToken
    darkIcon: VesselColorToken
    greyScale: VesselColorToken
    greyPrimary: VesselColorToken
    alertbg: VesselColorToken
    activeButtonBackground: VesselColorToken
    defaultText: VesselColorToken
    inverseText: VesselColorToken
    card: {
      background?: VesselColorToken
      border?: VesselColorToken
      text?: VesselColorToken
    }
    shadow: VesselColorToken
    icon: VesselColorToken
    container: VesselColorToken
    dropdownMenuGroup: VesselColorToken
    white: string
    black: string
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    themeMode?: ThemeModes
    setTheme?: (...args: any[]) => void
  }

  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
    xxl: true
  }
}

interface ThemeContextType {
  themeMode: ThemeModes
  setTheme: (mode: ThemeModes) => void
}

export const ThemeContext = createContext<ThemeContextType>({
  themeMode: ThemeModes.Light,
  setTheme: () => {},
})

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode: muiMode, setMode: setMuiMode } = useColorScheme()
  const [mode, setMode] = useState<ThemeModes>(muiMode === 'dark' ? ThemeModes.Dark : ThemeModes.Light)

  useEffect(() => {
    if (muiMode) {
      const newMode = muiMode === 'dark' ? ThemeModes.Dark : ThemeModes.Light
      setMode(newMode)
    }
  }, [muiMode])
  // this is the primary color we are to use, 00314A, don`t change it.
  // no 00526C
  const theme = createTheme({
    colorSchemes: {
      dark: {
        palette: {
          mode: 'dark',
          AppBar: {
            darkBg: '#00314A',
          },
          primary: {
            main: '#00314A',
            dark: '#00304A',
            light: '#0074B2',
            contrastText: '#F2F2F2',
          },
          secondary: {
            main: '#02799c',
            dark: '#606060',
            light: '#D9E1E7',
          },
          success: {
            main: '#2E7D32',
            dark: '#1B5E20',
            light: '#4CAF50',
          },
          error: {
            main: '#b5423e',
            dark: '#C62828',
            light: '#EF5350',
          },
          warning: {
            main: '#ED6C02',
            light: '#FF9800',
          },
          mainWarning: {
            main: '#f58700',
            dark: '#f58700',
            light: '#FF9800',
          },
          background: {
            main: '#303030',
            dark: '#303030',
          },
          divider: '#e6e6e6',
          hover: {
            main: '#e6e6e6a0',
            dark: '#e6e6e6a0',
          },
          paperOnPaper: {
            main: '#cbcbcb',
          },
          primaryText: {
            main: '#00314A',
            dark: '#F5F5F5',
            light: '#46BDDF',
            contrastText: '#F2F2F2',
            text: '#F2F2F2',
          },
          primaryWhite: {
            main: '#F2F2F2',
            dark: '#F2F2F2',
            light: '#F2F2F2',
          },
          secondaryText: {
            main: '#07DF89',
            dark: '#07A15D',
          },
          complementaryBlue: {
            main: '#00526b36',
            light: '#0cc6ff',
            dark: '#09169e',
            contrastText: '#ffffff',
          },
          activeGreen: {
            main: '#e8f5e9',
            light: '#e8f5e9',
            dark: '#e8f5e9',
          },
          alternateRowGrey: {
            main: '#f9f9f9',
            light: '#f9f9f9',
            dark: '#f9f9f9',
          },
          calmRed: {
            main: '#e57373',
            light: '#f6cfcf',
            dark: '#b5423e',
          },
          info: {
            main: '#37474f',
            light: '#62727b',
            dark: '#e3f2f7',
          },
          errorText: {
            main: '#ED506B',
            light: '#ED506B',
            dark: '#ED506B',
          },
          textLink: {
            main: '#0C80B9',
            light: '#1Ca2dC',
            dark: '#0C80B9',
          },
          icon: {
            main: '#ffffff',
            background: '#333333',
            hover: '#555555',
          },
          alternateRowsEven: {
            main: '#1E1E1E',
            light: '#0C80B9',
            dark: '#3C92A9',
          },
          alternateRowsOdd: {
            main: '#303030',
            light: '#FFFFFF',
            dark: '#303030',
          },
          primarySelected: {
            main: '#3C474B',
            dark: '#303C40',
          },
          darkIcon: {
            main: '#D4D4D4',
            light: '#ffffff',
            dark: '#AFAFAF',
          },
          greyScale: {
            main: '#cbcbcb',
            light: '#C4C4C4',
            dark: '#AFAFAF',
            text: '#919090',
          },
          greyPrimary: {
            main: '#00304A',
            light: '#4C4C4C',
            dark: '#d5d5d5',
            text: '#b7b7b7',
          },
          alertbg: {
            main: '#b5423e',
            light: '#e5841a',
            dark: '#00304A',
            text: '#00304A',
          },
          dropdownMenuGroup: {
            main: '#F0F0F0',
          },
          activeButtonBackground: {
            main: '#234dc2',
          },
          defaultText: {
            main: '#ffffff',
            contrastText: '#00526C',
          },
          inverseText: {
            main: '#00526C',
            contrastText: '#ffffff',
          },
          card: {
            background: {
              main: '#2d2d2d',
              dark: '#1a1a1a',
            },
            border: {
              main: '#444444',
            },
            text: {
              main: '#f5f5f5',
              dark: '#ffffff',
            },
          },
          container: {
            main: '#2d2d2d',
            dark: '#1a1a1a',
          },
          shadow: {
            main: '#00000050',
          },
          white: '#FFFFFF',
          black: '#000000',
        },
      },
      light: {
        palette: {
          mode: 'light',
          AppBar: {
            darkBg: '#00314A',
            // color: '#ffffff',
          },
          primary: {
            main: '#00314A',
            dark: '#00304A',
            light: '#0074B2',
            contrastText: '#F2F2F2',
          },
          secondary: {
            main: '#21B573',
            dark: '#606060',
            light: '#D9E1E7',
          },
          success: {
            main: '#2E7D32',
            dark: '#1B5E20',
            light: '#4CAF50',
          },
          error: {
            main: '#D32F2F',
            dark: '#C62828',
            light: '#EF5350',
          },
          warning: {
            main: '#ED6C02',
            light: '#FF9800',
          },
          mainWarning: {
            main: '#FF9800',
            dark: '#E65100',
            light: '#FF9800',
          },
          background: {
            main: '#F1F5F7',
            dark: '#303030',
          },
          divider: '#e6e6e6',
          hover: {
            main: '#e8f0fe',
            dark: '#e6e6e6a0',
            light: '#e6e6e6a0',
          },
          paperOnPaper: {
            main: '#cbcbcb',
            dark: '#A0A0A0',
            light: '#E4E4E4',
          },
          primaryText: {
            main: '#00314A',
            dark: '#00304A',
            light: '#0288d1',
            contrastText: '#F2F2F2',
            text: '#00304A',
          },
          primaryWhite: {
            main: '#F2F2F2',
            dark: '#F2F2F2',
            light: '#F2F2F2',
          },
          secondaryText: {
            main: '#219A58',
            dark: '#208847',
          },
          complementaryBlue: {
            main: '#00526b36',
            light: '#0cc6ff',
            dark: '#09169e',
            contrastText: '#ffffff',
          },
          activeGreen: {
            main: '#e8f5e9',
            light: '#e8f5e9',
            dark: '#e8f5e9',
          },
          alternateRowGrey: {
            main: '#f9f9f9',
            light: '#f9f9f9',
            dark: '#f9f9f9',
          },
          calmRed: {
            main: '#e57373',
            light: '#f6cfcf',
            dark: '#b5423e',
          },
          info: {
            main: '#F1F5F7',
            light: '#F1F5F7',
            dark: '#e3f2f7',
          },
          errorText: {
            main: '#B40000',
            light: '#BF0000',
            dark: '#A50000',
          },
          textLink: {
            main: '#0C80B9',
            light: '#085B9C',
            dark: '#0C80B9',
          },
          icon: {
            main: '#00526C',
            background: '#E8E8E8',
            hover: '#d0d0d0',
          },
          alternateRowsEven: {
            main: '#e7f5f98b',
            light: '#0C80B9',
            dark: '#3C92A9',
          },
          alternateRowsOdd: {
            main: '#FFFFFF',
            light: '#FFFFFF',
            dark: '#BABABA',
          },
          primarySelected: {
            main: '#EBEFF1',
            dark: '#EBEFF1',
          },
          darkIcon: {
            main: '#242424',
            light: '#2B2B2B',
            dark: '#000000',
          },
          greyScale: {
            main: '#282828',
            light: '#4C4C4C',
            dark: '#d2d2d2',
            text: '#919090',
          },
          greyPrimary: {
            main: '#eeeeee',
            light: '#4C4C4C',
            dark: '#AFAFAF',
            text: '#919090',
          },
          alertbg: {
            main: '#b5423e',
            light: '#e5841a',
            dark: '#00304A',
            text: '#00304A',
          },
          dropdownMenuGroup: {
            main: '#F0F0F0',
          },
          activeButtonBackground: {
            main: '#d3edff',
          },
          defaultText: {
            main: '#00526C',
            contrastText: '#ffffff',
          },
          inverseText: {
            main: '#ffffff',
            contrastText: '#00526C',
          },
          card: {
            background: {
              main: '#ffffff',
              dark: '#303030',
            },
            border: {
              main: '#EAEAEA',
            },
            text: {
              main: '#000000',
              dark: '#f5f5f5',
            },
          },
          container: {
            main: '#ffffff',
            dark: '#000000ff',
          },
          shadow: {
            main: '#00000029',
          },
          white: '#FFFFFF',
          black: '#000000',
        },
      },
    },
    typography: {
      fontFamily: ['Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      h1: {
        color: '#000000',
        fontSize: '20px',
        fontWeight: 700,
      },
      h2: {
        fontSize: '18px',
        fontWeight: 700,
      },
      caption: {
        color: '#5f6368',
        fontSize: 12,
        fontWeight: 500,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        xxl: 2400,
      },
    },
  })

  theme.components = {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(120, 120, 120, 0.12)',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 14,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
        contained: {
          boxShadow: '0px 10px 20px -6px rgb(0 0 0 / 12%)',
        },
      },
      variants: [
        {
          props: { variant: 'assetPrimary' },
          style: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        },
        {
          props: { variant: 'assetSecondary' },
          style: {
            backgroundColor: theme.palette.secondary.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: theme.palette.secondary.dark,
            },
          },
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        },
        indicator: {
          backgroundColor: theme.palette.secondary.main,
          height: '4px',
          marginBottom: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'white !important',
        },
      },
    },
  }

  const themeContextValue = {
    themeMode: mode,
    setTheme: (newMode: ThemeModes) => {
      setMode(newMode)
      if (setMuiMode) {
        setMuiMode(newMode === ThemeModes.Dark ? 'dark' : 'light')
      }
      if (typeof window !== 'undefined') {
        localStorage.setItem('mui-mode', newMode === ThemeModes.Dark ? 'dark' : 'light')
      }
    },
  }

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <GlobalStyles
        styles={{
          body: {
            backgroundColor: `${theme?.palette?.background?.main ?? '#eee'} !important`,
            height: '100%',
            minHeight: 'calc(100vh - 160px)',
          },
          '.asset-theme': {
            backgroundColor: theme.palette.background?.main,
            color: theme.palette.defaultText?.main,
          },
          '.asset-card': {
            backgroundColor: theme.palette.card?.background?.main,
            border: `1px solid ${theme.palette.card?.border?.main}`,
            color: theme.palette.card?.text?.main,
            boxShadow: `0px 3px 6px ${theme.palette.shadow?.main}`,
          },
        }}
      />
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme} defaultMode={mode === ThemeModes.Dark ? 'dark' : 'light'}>
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  )
}
